<template>
  <div class="campaign-subscriber-detail d-flex flex-column">
    <div class="campaign-subscriber-detail--header d-flex align-items-center gap-3">
      <div
        class="back-button"
        v-b-tooltip.hover
        title="Back"
        @click="goBack()"
      >
        <b-icon-arrow-left-circle scale="1.2" />
      </div>
      <h4 class="campaign-subscriber-detail--title">
        Campaign Subscriber Detail
      </h4>
    </div>
    <b-overlay :show="loadingSubscriber">
      <div class="campaign-subscriber-detail--content bg-white-custom border px-3 py-3">
        <ConveyRequiredStatement class="mb-2" />
        <b-form class="subscriber-form">
          <b-form-group label="Phone Number:" class="required-form-field" style="font-weight: 500;">
            <b-form-input
              v-model="subscriber.number"
              v-mask="'(###) ###-####'"
              placeholder="Phone Number"
              :disabled="inUpdate || inUnsubscribe"
              readonly
            />
            <b-form-invalid-feedback :state="!!errors['phone'] ? false : true">
              {{ errors['phone'] }}
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="Groups:" style="font-weight: 500;">
            <MultiSelectDropdown
              placeholder="Select Groups"
              v-model="subscriber.groups"
              showSearch
              showSelectAll
              showDeselectAll
              :options="groupOptions"
              :disabled="inUpdate || inUnsubscribe"
            />
          </b-form-group>
          <b-form-group label="Zip Code:" class="required-form-field" style="font-weight: 500;">
            <b-form-input
              v-model="subscriber.zipcode"
              placeholder="Primary Zip Code"
              v-mask="'#####'"
              :disabled="inUpdate || inUnsubscribe"
            />
            <b-form-invalid-feedback :state="!!errors['zipcode'] ? false : true">
              {{ errors['zipcode'] }}
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="Language:" style="font-weight: 500;">
            <SelectDropdown
              showSearch
              placeholder="Select Language"
              v-model="subscriber.language"
              :options="languages"
              :disabled="inUpdate || inUnsubscribe"
            />
          </b-form-group>
          <div class="d-flex justify-content-end align-items-center gap-2">
            <b-button
              pill
              variant="success"
              size="sm"
              :disabled="loadingSubscriber || inUpdate || inUnsubscribe"
              @click="updateSubscriber"
            >
              <template v-if="!inUpdate">{{ subscriber.status !== "optout" ? "Update" : "Subscribe"}}</template>
              <b-spinner v-else small></b-spinner>
            </b-button>
            <b-button
              v-if="subscriber.status !== 'optout'"
              pill
              variant="danger"
              size="sm"
              :disabled="loadingSubscriber || inUpdate || inUnsubscribe"
              @click="unsubscribe"
            >
              <template v-if="!inUnsubscribe">Unsubscribe</template>
              <b-spinner v-else small></b-spinner>
            </b-button>
          </div>
        </b-form>
      </div>
    </b-overlay>
  </div>
</template>

<script>
/* eslint-disable */
import ConveyRequiredStatement from "@/components/ConveyRequiredStatement";
import SelectDropdown from "@/components/common/SelectDropdown";
import MultiSelectDropdown from "@/components/common/MultiSelectDropdown";
import errorHandler from "@/mixins/errorHandler";
import formatter from "@/mixins/formatter";
import { mapGetters } from "vuex";

export default {
  name: "CampaignSubscriberDetail",
  mixins: [formatter, errorHandler],
  components: {
    ConveyRequiredStatement,
    MultiSelectDropdown,
    SelectDropdown,
  },
  data() {
    return {
      inUpdate: false,
      inUnsubscribe: false,
      loadingSubscriber: false,
      fetchAbortController: null,
      subscriber: {},
      errors: {},
    };
  },
  computed: {
    ...mapGetters("agency", ["textDialects"]),
    ...mapGetters("campaigns", ["currentCampaigns"]),
    languages() {
      const langs = this.textDialects.map((c) => ({ value: c.code, label: c.label }));
      const esEleIndex = langs.findIndex((lang) => lang.value === "es");
      if (esEleIndex !== -1) {
        const [element] = langs.splice(esEleIndex, 1);
        langs.unshift(element);
      }

      const engEleIndex = langs.findIndex((lang) => lang.value === "en");
      if (engEleIndex !== -1) {
        const [element] = langs.splice(engEleIndex, 1);
        langs.unshift(element);
      }
      return langs;
    },
    campaignId() {
      return this.$route.params.campaignId;
    },
    campaignSubscriberId() {
      return this.$route.params.campaignSubscriberId;
    },
    groupOptions() {
      const campaign = this.currentCampaigns.find(
        item => item.campaign_id === this.$route.params.campaignId
      );
      if (campaign) {
        return (campaign.groups || []).map((group) => {
          const { id, group_name } = group;
          return {
            label: group_name,
            value: id,
          };
        });
      }
      return [];
    }
  },
  created() {
    this.errors = {};
    this.fetchSubscriberDetail();
  },
  methods: {
    goBack() {
      this.$router.push({ name: "Campaign Subscribers" }).catch(() => {});
    },
    abortFetchOperations() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    async fetchSubscriberDetail() {
      try {
        this.fetchAbortController = new AbortController();
        this.loadingSubscriber = true;
        const resp = await this.$ConveyApi.campaignsApi.getCampaignSubscriber({
          campaignId: this.campaignId,
          subscriberId: this.campaignSubscriberId,
          signal: this.fetchAbortController.signal,
        });
        const { number, language, groups } = resp.data.data;
        this.subscriber = {
          ...resp.data.data,
          number: this.prettyPhoneNumber(number),
          language: language || "en",
          groups: groups && groups.length > 0 ? groups.map(group => group.group_id) : []
        };
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log(
            "[CampaignSubscriberDetail:fetchSubscriberDetail] ERROR: ",
            err
          );
        }
      } finally {
        this.loadingSubscriber = false;
      }
    },
    validate() {
      this.errors = {};
      const { number, zipcode } = this.subscriber;
      const realNumber = `${number}`.replace(/\D/g,'');
      const realZipcode = `${zipcode}`.replace(/\D/g,'');
      if (!realNumber) {
        this.errors['phone'] = "Phone number is required."
      } else if (realNumber.length !== 10) {
        this.errors['phone'] = "Input valid phone number."
      }

      if (!realZipcode) {
        this.errors['zipcode'] = "Zip code is required."
      } else if (realZipcode.length !== 5) {
        this.errors['zipcode'] = "Input valid zip code."
      }

      if (Object.keys(this.errors).length > 0) {
        return false;
      }
      return {
        number: `+1${realNumber}`,
        zipcode: realZipcode,
      };
    },
    async updateSubscriber($event) {
      $event.preventDefault();
      const validated = this.validate();
      if (!validated) {
        return;
      }
      try {
        const { language, groups } = this.subscriber;
        const { number, zipcode } = validated;
        this.inUpdate = true;
        const payload = { language, groups, number, zipcode };
        await this.$ConveyApi.campaignsApi.addSubscriberToCampaign({
          campaignId: this.campaignId,
          payload
        });
        this.$toastr("success", this.subscriber.status === "optout"
          ? "Subscribed successfully."
          : "Updated subscriber successfully."
        );
      } catch(err) {
        this.$toastr("error", "An error has occurred. Please try again.");
        console.log("[CampaignSubscriberDetail:updateSubscriber] ERROR: ", err);
      } finally {
        this.inUpdate = false;

        setTimeout(() => {
          this.goBack();
        }, 500)
      }
    },
    async unsubscribe($event) {
      $event.preventDefault();
      try {
        this.inUnsubscribe = true;
        const { subscriber_id } = this.subscriber;
        await this.$ConveyApi.campaignsApi.deleteCampaignSubscriber({
          campaignId: this.campaignId,
          subscriberId: subscriber_id
        });
        this.$toastr("success", "Unsubscribed successfully.");
      } catch(err) {
        this.$toastr("error", "An error has occurred. Please try again.");
        console.log("[CampaignSubscriberDetail:unsubscribe] ERROR: ", err);
      } finally {
        this.inUnsubscribe = false;

        setTimeout(() => {
          this.goBack();
        }, 500)
      }
    }
  },
  beforeDestroy() {
    this.abortFetchOperations();
  }
};
</script>

<style lang="scss">
.campaign-subscriber-detail {
  width: 100%;
  height: 100%;
  gap: 1rem;

  &--title {
    margin: 0;
    height: 25px;
    line-height: 25px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .b-overlay-wrap {
    width: 100%;
    max-width: 800px;
  }

  &--content {
    max-width: 800px;
    width: 100%;
    height: auto;
    border-radius: 1rem;
  }
}
</style>
